import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { Path } from 'react-hook-form';
import { ErrorContextToFieldConverter } from '@alfa-retail-react/components';

import { LoadStateStore } from 'app/loading/LoadStateStore';
import { OrderService } from 'app/service/OrderService';
import { CardOrder } from 'app/model/CardOrder';
import { EmploymentFormStore } from 'app/logic/pages/employment/EmploymentFormStore';
import { ReferenceService } from 'app/service/ReferenceService';
import { EmploymentFormValues } from 'app/logic/pages/employment/EmploymentPage';
import { MapBuilder } from 'app/utils/MapBuilder';
import { LocalityTypeStore } from 'app/logic/address/LocalityTypeStore';
import { RestoreFormData } from 'app/types/store';

export class EmploymentPageStore {
    private readonly BELARUS_COUNTRY_CODE = '112';

    constructor() {
        makeObservable(this);
    }

    readonly loadStateStore: LoadStateStore = new LoadStateStore();

    readonly actionStateStore: LoadStateStore = new LoadStateStore(true);

    private readonly orderService = new OrderService();

    private readonly referenceService = new ReferenceService();

    private readonly localityTypesStore = new LocalityTypeStore();

    formStore: EmploymentFormStore;

    @observable
    private _order: CardOrder;

    /**
     * Маппинг контекстов ошибок валидации к полям формы.
     * Преобразует путь к полю модели в путь к полю формы.
     *
     * В случае, когда путь к полю формы === null - ошибка отображается к странице.
     * @private
     */
    readonly contextConverter =
        new ErrorContextToFieldConverter<EmploymentFormValues>(
            MapBuilder.create<string, Array<Path<EmploymentFormValues>>>()
                .value('deliveryAddress', [])
                .value('deliveryAddress.soatoCode', [
                    'deliveryAddress.locality',
                ])
                // .value("deliveryAddress.countryCode, ["deliveryAddress.locality',
                .value('deliveryAddress.region', ['deliveryAddress.locality'])
                .value('deliveryAddress.district', ['deliveryAddress.locality'])
                .value('deliveryAddress.villageCouncil', [
                    'deliveryAddress.locality',
                ])
                .value('deliveryAddress.localityType', [
                    'deliveryAddress.locality',
                ])
                .value('deliveryAddress.locality', ['deliveryAddress.locality'])
                .value('deliveryAddress.streetType', ['deliveryAddress.street'])
                .value('deliveryAddress.street', ['deliveryAddress.street'])
                .value('deliveryAddress.house', ['deliveryAddress.house'])
                .value('deliveryAddress.building', ['deliveryAddress.building'])
                .value('deliveryAddress.flat', ['deliveryAddress.flat'])
                .value('deliveryAddress.postalCode', [
                    'deliveryAddress.postalCode',
                ])

                .value('client.employment', [])
                .value('client.employment.activityKindCode', [
                    'activityKindCode',
                ])
                .value('client.employment.activityFieldCode', [
                    'activityFieldCode',
                ])
                .value('client.employment.unp', ['unp'])
                .value('client.employment.incomeAmount', ['incomeAmount'])
                .value('client.employment.experience', ['experience'])
                .defaultValue([])
                .build(),
        );

    @action
    async initialize(orderId: string) {
        return this.loadStateStore.loadData(async () => {
            return Promise.all([
                this.orderService.getById(orderId),
                this.orderService.getEmploymentDetails(),
                this.referenceService.getEmploymentsReference(),
                this.referenceService.getIndustriesReference(),
                this.referenceService.getExperiencesReference(),
                this.referenceService.getStreetTypesReference(),
                this.referenceService.getCountryById(this.BELARUS_COUNTRY_CODE),
                this.localityTypesStore.initialize(),
            ]).then(
                ([
                    order,
                    employmentDetails,
                    employmentsReference,
                    industriesReference,
                    experienceReference,
                    streetTypesReference,
                    country,
                ]) => {
                    const isEmploymentDetailsFilled = !!employmentDetails.unp || !!employmentDetails.incomeAmount;
                    const orderWithEmploymentDetails = isEmploymentDetailsFilled ? {
                        ...order,
                        client: {
                            ...order.client,
                            employment: {
                                /**
                                 * Данные о зарплате и УНП, внесенные клиентом, имеют больший приоритет чем запрошенные
                                 */
                                ...employmentDetails,
                                ...(order.client.employment || {}),
                            }
                        }
                    } : order;

                    this.setOrder(orderWithEmploymentDetails);

                    this.formStore = new EmploymentFormStore(
                        orderWithEmploymentDetails,
                        employmentsReference,
                        industriesReference,
                        experienceReference,
                        streetTypesReference,
                        country,
                        this.localityTypesStore,
                    );

                    return this.formStore.initialize();
                },
            );
        });
    }

    /**
     * Возвращает данные из заполненной формы текущего шага
     */
    @computed
    get restorationFormData(): RestoreFormData<EmploymentFormStore> {
        return { ...this.formStore };
    }

    /**
     * Заполняет форму данными, сохраненными ранее при заполнении
     */
    @action
    restoreFormData(formData: RestoreFormData<EmploymentFormStore>) {
        runInAction(() => {
            this.formStore.activityKindCode = formData.activityKindCode;
            this.formStore.unp = formData.unp;
            this.formStore.incomeAmount = formData.incomeAmount;
            this.formStore.activityFieldCode = formData.activityFieldCode;
            this.formStore.experience = formData.experience;
            this.formStore.soleOwner = formData.soleOwner;
            this.formStore.sameAddress = formData.sameAddress;

            this.formStore.deliveryAddressFormStore = formData.deliveryAddressFormStore;
        });
    }

    @computed
    get order() {
        return this._order;
    }

    @action
    setOrder(order: CardOrder) {
        this._order = order;
    }
}
